import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer, SafeResourceUrl, SafeUrl } from '@angular/platform-browser';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { OverlayState, FilePreviewOverlayService } from './file-preview-overlay.service';

@Component({
  selector: 'cloudia-file-preview-overlay',
  templateUrl: './file-preview-overlay.component.html',
  styleUrls: ['./file-preview-overlay.component.scss'],
})
export class FilePreviewOverlayComponent implements OnInit, OnDestroy {
  state: OverlayState | null = null;
  safeImageUrl: SafeUrl | null = null;
  safePdfUrl: SafeResourceUrl | null = null;

  private fileType: 'image' | 'pdf' | 'other' = 'other';
  private blobUrl: string | null = null;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly filePreviewService: FilePreviewOverlayService,
    private readonly sanitizer: DomSanitizer
  ) {}

  get isVisible(): boolean {
    return this.state !== null;
  }

  get isLoading(): boolean {
    return this.state?.phase === 'loading';
  }

  get fileName(): string {
    if (!this.state) return '';
    return this.state.phase === 'loading' ? this.state.fileName : this.state.config.fileName;
  }

  get isImage(): boolean {
    return this.fileType === 'image';
  }

  get isPdf(): boolean {
    return this.fileType === 'pdf';
  }

  ngOnInit(): void {
    this.filePreviewService.state$.pipe(takeUntil(this.destroy$)).subscribe((state) => {
      this.revokeBlobUrl();
      this.state = state;

      if (state?.phase === 'ready') {
        const { fileName, blob } = state.config;
        const ext = fileName.split('.').pop()?.toLowerCase() ?? '';
        if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) {
          this.fileType = 'image';
        } else if (ext === 'pdf') {
          this.fileType = 'pdf';
        } else {
          this.fileType = 'other';
        }
        this.blobUrl = URL.createObjectURL(blob);
        this.safeImageUrl = this.fileType === 'image' ? this.sanitizer.bypassSecurityTrustUrl(this.blobUrl) : null;
        this.safePdfUrl = this.fileType === 'pdf' ? this.sanitizer.bypassSecurityTrustResourceUrl(this.blobUrl) : null;
      } else {
        this.fileType = 'other';
        this.safeImageUrl = null;
        this.safePdfUrl = null;
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.revokeBlobUrl();
  }

  close(): void {
    this.filePreviewService.close();
  }

  download(): void {
    if (this.state?.phase === 'ready') {
      this.state.config.downloadFn();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.state) {
      this.close();
    }
  }

  private revokeBlobUrl(): void {
    if (this.blobUrl) {
      URL.revokeObjectURL(this.blobUrl);
      this.blobUrl = null;
    }
  }
}
